import { Component, Input } from "@angular/core";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamLocation } from "@common/ADAPT.Common.Model/organisation/team-location";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { combineLatest, merge, Observable, of, ReplaySubject, shareReplay } from "rxjs";
import { filter, map, startWith, switchMap, withLatestFrom } from "rxjs/operators";
import { CommonTeamsService } from "../../common-teams.service";
import { TeamHierarchyNodeType, TeamHierarchyService } from "../../team-hierarchy.service";
import { TeamDashboardSharedService } from "../team-dashboard-shared.service";

@Component({
    selector: "adapt-link-team",
    templateUrl: "./link-team-dashboard.component.html",
    styleUrls: ["link-team-dashboard.component.scss"],
    standalone: false,
})
export class LinkTeamDashboardComponent extends BaseComponent {
    @Input() public set teamId(value: number | undefined) {
        if (value) {
            this.teamId$.next(value);
        }
    }
    private teamId$ = new ReplaySubject<number>(1);
    @Input() public set team(value: Team | undefined) {
        if (value) {
            this.team$.next(value);
        }
    }
    public team$ = new ReplaySubject<Team>(1);

    @Input() public showName = true;
    @Input() public showIcon = false;
    @Input() public showIconBorder = false;
    @Input() public showIconTooltip = true;
    @Input() public smallIcon = false;
    @Input() public labelLinkDisabled = false;
    @Input() public showLabels = false;
    @Input() public set focusPersonId(value: number | undefined) {
        if (value) {
            this.focusPersonId$.next(value);
        }
    }
    public focusPersonId$ = new ReplaySubject<number>(1);

    @Input() public showChildren = false;
    @Input() public showAllChildren = false;
    @Input() public showChildCount?: boolean;
    @Input() public childrenExpanded = false;
    @Input() public filterTeams?: (teams: Team[]) => Team[] | Observable<Team[]>;

    public dashboardUrl$: Observable<string | undefined>;
    public showTeamLeaderIcon$: Observable<boolean>;
    public teamLeaderLabel$: Observable<string>;
    public allChildTeamCount$: Observable<number>;
    public childTeams$: Observable<Team[]>;
    public icon$: Observable<string>;
    public iconColour$: Observable<string>;
    public iconTooltip$: Observable<string>;

    public constructor(
        teamDashboardSharedService: TeamDashboardSharedService,
        commonTeamService: CommonTeamsService,
        teamHierarchyService: TeamHierarchyService,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super();

        this.teamLeaderLabel$ = commonTeamService.getTeamLeaderLabel$();
        this.teamId$.pipe(
            switchMap((teamId) => commonTeamService.getTeamById(teamId)),
            this.takeUntilDestroyed(),
        ).subscribe((team) => {
            if (team) {
                this.team$.next(team);
            }
        });

        this.dashboardUrl$ = this.team$.pipe(
            switchMap((team) => teamDashboardSharedService.getTeamDashboardLink(team)),
        );

        this.showTeamLeaderIcon$ = combineLatest([this.team$, this.focusPersonId$]).pipe(
            map(([team, focusPersonId]) => team.teamLeaderPersonId === focusPersonId),
        );

        const teamChanged$ = this.team$.pipe(
            switchMap((team) => merge(
                rxjsBreezeService.entityPropertyChangedWithInitialEntity(team),
                rxjsBreezeService.entityPropertyChangedWithInitialEntity(team.teamTypeConfiguration),
            ).pipe(
                // need to check if entityAspect exists as we use dummy teams for group config etc.
                filter(() => !team.entityAspect || !team.entityAspect.entityState.isDetached()),
                map(() => team),
            )),
        );

        this.iconColour$ = teamChanged$.pipe(
            map((team) => team.extensions.getIconColour() ?? "var(--adapt-team)"),
        );

        this.icon$ = teamChanged$.pipe(
            map((team) => team.extensions.getIcon()),
        );

        const teamLocationChanged$ = combineLatest([
            this.team$,
            // any team location changed (and saved)
            rxjsBreezeService.entityTypeChanged(TeamLocation).pipe(startWith(undefined)),
            // any team location for team modified (this does not trigger entityTypeChanged)
            rxjsBreezeService.breezeEntityChanged$.pipe(
                withLatestFrom(this.team$),
                filter(([prop, team]) => prop.entity instanceof TeamLocation && prop.entity.teamId === team.teamId),
                startWith(undefined),
            ),
        ]).pipe(
            // need to check if entityAspect exists as we use dummy teams for group config etc.
            filter(([team]) => !team.entityAspect || !team.entityAspect.entityState.isDetached()),
            map(([team]) => team),
        );

        this.iconTooltip$ = teamLocationChanged$.pipe(
            switchMap(async (team) => [team, await teamHierarchyService.promiseToGetImmediateParents(team)] as const),
            // check if detached again as it could be detached during the parent lookup above
            filter(([team]) => !team.entityAspect || !team.entityAspect.entityState.isDetached()),
            map(([team, parents]) => {
                if (parents.length > 0) {
                    return `This <b>${team.teamTypeConfiguration.name}</b> sits within: <ul class="list-content mb-0">`
                        + parents.map((p) => `<li>${p.name} (${p.type === TeamHierarchyNodeType.Zone ? "Zone" : p.entity?.teamTypeConfiguration.name})</li>`).join("\n")
                        + `</ul>`;
                }
                return team.teamTypeConfiguration.name;
            }),
        );

        const allChildTeams$ = teamLocationChanged$.pipe(
            switchMap((team) => this.shouldShowChildCount
                ? commonTeamService.getActiveChildTeams(team)
                : of([])),
            shareReplay(1),
        );

        this.childTeams$ = allChildTeams$.pipe(
            switchMap((teams) => this.applyTeamFilter(teams)),
        );

        this.allChildTeamCount$ = allChildTeams$.pipe(
            map((teams) => teams.length),
        );
    }

    public get shouldShowChildCount() {
        return this.showChildCount || (this.showChildCount === undefined && this.showChildren);
    }

    private applyTeamFilter(teams: Team[]) {
        if (!teams) {
            return of([]);
        }

        if (!this.filterTeams) {
            return of(teams);
        }

        const filtered = this.filterTeams(teams);
        return Array.isArray(filtered)
            ? of(filtered)
            : filtered;
    }
}
